<template>
  <p>
    Administra todos los correos en rangos de hasta 15 días. Si eres administrador verás todos, y si
    eres agente solo verás los de tu buzón de correos.
    <a :href="DOCS_URL" target="_blank" rel="noopener noreferrer">Click aquí</a> para conocer más.
  </p>

  <emails-table-controls
    style="margin-bottom: 20px"
    :selectedRows="selectedTableRowKeys"
    :downloading="downloading"
    @unselectRows="handleUnselectRows"
    @form-fields-completed="handleFormFieldsCompleted"
    @open-advanced-filters-drawer="handleOpenAdvancedFiltersDrawer"
    @update-data="fetchEmailsData"
    @file-download="handleDownload"
    @show-table="handleShowTable"
  />

  <EmailCounter
    :counter="selectedTableRowKeys.length"
    :isAllSelected="selectedTableRowKeys.length == emailsData.length"
  />
  <ActionsCard
    v-if="selectedTableRowKeys.length > 0"
    :role="isAdmin"
    :isAssigned="true"
    :showFullActions="true"
    :selectedRows="emailsData.results?.filter((d) => selectedTableRowKeys.includes(Number(d.key)))"
    :showMoveButton="false"
    :isInbox="false"
    @massiveTag="handleMassiveTag"
    @update="fetchEmailsData"
    @resolved="resolvedEmails"
  />
  <emails-table
    v-if="showTable"
    :loadingTable="loadingTable"
    :data="emailsData"
    @page-change="handleTablePageChange"
    @open-ticket-history-drawer="handleOpenTicketHistoryDrawer"
    @open-email-history-drawer="handleOpenEmailHistoryDrawer"
    @open-edit-drawer="handleOpenEditDrawer"
    @open-contact-drawer="handleOpenContactDrawer"
    @open-user-modal="handleOpenUserModal"
    @update-data="fetchEmailsData"
  />

  <ticket-drawer-contact
    v-if="ticketHistoryDrawerConfig.visible"
    :item="ticketHistoryDrawerConfig.ticket"
    @onCloseDrawerTicket="handleCloseDrawer"
    :isModuleTicket="isModuleTicket"
  />

  <!-- {{ editDrawerConfig.ticket }} -->
  <edit-email-drawer
    v-if="editDrawerConfig.visible"
    :visible="editDrawerConfig.visible"
    :ticket="editDrawerConfig.ticket"
    @close-drawer="handleCloseDrawer"
  />

  <emails-advanced-filters
    v-if="advancedFiltersDrawerVisible"
    :visible="advancedFiltersDrawerVisible"
    :empty="emailsData.results.length === 0 && !advancedFilters"
    :selectedFilters="advancedFilters"
    @apply-filters="handleApplyAdvancedFilters"
    @clear-filters="handleClearAdvancedFilters"
    @close-drawer="handleCloseDrawer"
  />

  <contact-detail-drawer
    fromSchedule
    v-if="contactDrawerConfig.visible"
    :itemId="contactDrawerConfig.contactId"
    :visible="contactDrawerConfig.visible"
    :campaignName="contactDrawerConfig.campaign?.name"
    :portfolioId="contactDrawerConfig.campaign?.id"
    @on-close-drawer="handleCloseDrawer"
  />

  <users-invite-update
    fromTickets
    :record="userModalConfig.record"
    v-if="userModalConfig.visible"
    :visible="userModalConfig.visible"
    @on-close-modal="handleCloseDrawer"
  />

  <DetailDrawer
    v-if="emailHistoryDrawerConfig.visible"
    :emailId="emailHistoryDrawerConfig.email.id"
    @onCloseDrawer="emailHistoryDrawerConfig.visible = false"
  />
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, provide } from "vue";
import { useStore } from "vuex";
import { message } from "ant-design-vue";
import { debounce } from "lodash";

import { downloadFile } from "@/app/shared/utils/file";
// import { dateToString } from "@/app/shared/utils/dates";
// import {
//   downloadTicketsWithBasicFilters,
//   downloadTicketsWithAdvancedFilters,
// } from "@/app/ticketsManager/services";

import EmailsTable from "@/app/emailsManager/components/tables/EmailsTable.vue";
import EmailsTableControls from "@/app/emailsManager/components/tables/EmailsTableControls.vue";
// import EditEmailDrawer from "@/app/emailsManager/components/drawers/EditEmailDrawer.vue";
import EmailsAdvancedFilters from "@/app/emailsManager/components/drawers/EmailsAdvancedFilters.vue";
import ContactDetailDrawer from "@/app/contacts/components/drawers/ContactDetailDrawer.vue";
import UsersInviteUpdate from "@/app/users/components/modals/UsersInviteUpdate.vue";
import TicketDrawerContact from "@/app/contacts/components/drawers/recordTickets/TicketDrawerContact.vue";
import ActionsCard from "@/app/emails/components/cards/ActionsCard.vue";
import EmailCounter from "@/app/emails/components/cards/EmailCounter.vue";
import DetailDrawer from "@/app/workspace/components/drawers/email/DetailDrawer.vue";

import { emailService } from "@/app/emails/services/Email.service";
import { emailManagerService } from "@/app/emailsManager/services/EmailManager.service";

import { UserData } from "@/@types/global";

const DOCS_URL = `${import.meta.env.VITE_WEB_DOC}/correo/gestor-de-correos-1/`;
const store = useStore();
const user = computed<UserData>(() => store.getters["getDataUser"]);
const showTable = ref(true);
const loadingTable = ref(false);
const isModuleTicket = ref(false);
const emailsData = computed(() => store.getters["getEmailsManager"]);
const tableEmailsPage = computed(() => store.getters["getTablePage"]);
const basicFilters = computed(() => store.getters["getEmailsBasicFilters"]);
const advancedFilters = computed(() => store.getters["getEmailsAdvancedFilters"]);
// const contactsInfo = computed(() => store.getters["getContactsInfoTable"]);
const selectedTableRowKeys = ref([]);

const ticketHistoryDrawerConfig = ref({
  visible: false,
  ticket: {},
});
const emailHistoryDrawerConfig = ref({
  visible: false,
  email: {},
});
const editDrawerConfig = ref({
  visible: false,
  ticket: {},
});
const contactDrawerConfig = ref({
  visible: false,
  contactId: null,
  campaign: null,
});
const advancedFiltersDrawerVisible = ref(false);
const userModalConfig = ref({
  visible: false,
  record: {},
});
const downloading = ref(false);
const isAdmin = ref<boolean>(true);

const usersData = computed(() => store.getters["getUsersData"]);

const handleTablePageChange = async (page: number) => {
  store.dispatch("setTablePage", page);
  await fetchEmailsData();
};

const handleFormFieldsCompleted = async (basicFilters) => {
  store.dispatch("setBasicFilters", basicFilters);

  store.dispatch("setTablePage", 1);
  await fetchEmailsData();
};

const handleShowTable = (show) => {
  showTable.value = show;
};

const fetchEmailsData = debounce(async function () {
  loadingTable.value = true;

  const filters = { ...basicFilters.value, ...advancedFilters.value, page: tableEmailsPage.value };

  console.log(filters, "filters");
  await store.dispatch("getAllEmailsManager", filters);
  if (tableEmailsPage.value === 1 && basicFilters.value) {
    console.log("entra aca 1");
    await store.dispatch("fetchEmailsAdvancedFiltersData", {
      basicFilters: basicFilters.value,
      user: user.value?.id,
    });
  }

  // if (!basicFilters.value) {
  //   console.log('entra aca 2');
  //   await store.dispatch("fetchDefaultEmails");
  // } else if (!advancedFilters.value) {
  //   console.log('entra aca 3');
  //   await store.dispatch("fetchEmailsWithBasicFilters", filters);
  // } else {
  //   console.log('entra aca 4');
  //   await store.dispatch("fetchEmailsWithAdvancedFilters", {
  //     ...basicFilters.value,
  //     ...advancedFilters.value,
  //     page: tableEmailsPage.value,
  //   });
  // }

  loadingTable.value = false;
}, 50);

const handleOpenTicketHistoryDrawer = (ticket) => {
  ticketHistoryDrawerConfig.value.visible = true;
  ticketHistoryDrawerConfig.value.ticket = ticket;
};

const handleOpenEmailHistoryDrawer = (record) => {
  emailHistoryDrawerConfig.value.visible = true;
  emailHistoryDrawerConfig.value.email = record;
};

const handleOpenEditDrawer = (ticket) => {
  editDrawerConfig.value.visible = true;
  editDrawerConfig.value.ticket = ticket;
};

const handleOpenAdvancedFiltersDrawer = () => {
  advancedFiltersDrawerVisible.value = true;
};

const handleApplyAdvancedFilters = async (advancedFilters) => {
  store.dispatch("setAdvancedFilters", advancedFilters);
  store.dispatch("setTablePage", 1);
  await fetchEmailsData();
  handleCloseDrawer();
  message.success("Se han aplicado los filtros correctamente");
};

const handleClearAdvancedFilters = async () => {
  store.dispatch("setAdvancedFilters", null);
  await fetchEmailsData();
};

const handleOpenContactDrawer = (contactId, campaign) => {
  contactDrawerConfig.value.visible = true;
  contactDrawerConfig.value.contactId = contactId;
  contactDrawerConfig.value.campaign = campaign;
};

const handleOpenUserModal = async ({ userName, userId } = {}) => {
  await store.dispatch("fetchUsersData", userName.split(" ").at(0).toLowerCase());
  userModalConfig.value.visible = true;
  userModalConfig.value.record = usersData.value.results.find((u) => u.id === userId);
};

const handleCloseDrawer = () => {
  editDrawerConfig.value.visible = false;
  advancedFiltersDrawerVisible.value = false;
  contactDrawerConfig.value.visible = false;
  userModalConfig.value.visible = false;
  ticketHistoryDrawerConfig.value.visible = false;
};

const handleDownload = async () => {
  downloading.value = true;

  try {
    let response = null;

    const filters = {
      ...basicFilters.value,
      ...advancedFilters.value,
    };

    response = await emailManagerService.downloadEmailsManager(filters);
    // if (!advancedFilters.value) {
    //   response = await downloadTicketsWithBasicFilters({ ...basicFilters.value });
    // } else {
    //   response = await downloadTicketsWithAdvancedFilters({
    //     ...basicFilters.value,
    //     ...advancedFilters.value,
    //   });
    // }

    downloadFile(response, "Tickets.xlsx", { useResponseFileName: true });
  } catch (error) {
    const errorMessage = error?.getErrorMessage() || "Ha ocurrido un error";

    message.error(errorMessage);
  }

  downloading.value = false;
};

const handleMassiveTag = async (tags: { tagsToAdd: number[]; tagsToRemove: number[] }) => {
  const response = await emailService.massiveTag(selectedTableRowKeys.value, tags);

  if (response.status == 200) message.success("Se ha asignado las etiquetas correctamente");

  fetchEmailsData();
};

const resolvedEmails = () => {
  selectedTableRowKeys.value = [];
  fetchEmailsData();
};

provide("selectedTableRowKeys", selectedTableRowKeys);
// provide("selectedRowsLastChannel", selectedRowsLastChannel);
// provide("selectedRowsList", selectedRowsList);
// provide(
//   "updateSelectedTableRowKeys",
//   (newSelectedRows) => (selectedTableRowKeys.value = newSelectedRows)
// );

onMounted(async () => {
  await fetchEmailsData();
  store.dispatch("setAdvancedFilters", null);
});
</script>

<style lang="sass" scoped>
p
  color: $gray-8
  margin-bottom: 12px

section
  display: grid
  justify-items: center
  color: $gray-6
  font-size: 20px
</style>
